import {
  collection, query, where, orderBy, limit, getDocs, deleteDoc, doc,
  addDoc, updateDoc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { DirectMessage } from '../types';

const COL = 'directMessages';

export async function getAllDMs(maxCount = 500): Promise<DirectMessage[]> {
  const q = query(collection(db, COL), orderBy('sentAt', 'desc'), limit(maxCount));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as DirectMessage));
}

export async function getUserDMs(userId: string): Promise<{ inbox: DirectMessage[]; sent: DirectMessage[] }> {
  const [inSnap, outSnap] = await Promise.all([
    getDocs(query(collection(db, COL), where('recipientId', '==', userId), orderBy('sentAt', 'desc'), limit(100))),
    getDocs(query(collection(db, COL), where('senderId', '==', userId), orderBy('sentAt', 'desc'), limit(100))),
  ]);
  return {
    inbox: inSnap.docs.map(d => ({ id: d.id, ...d.data() } as DirectMessage)),
    sent: outSnap.docs.map(d => ({ id: d.id, ...d.data() } as DirectMessage)),
  };
}

export async function sendDM(params: {
  senderId: string;
  senderNickname: string;
  recipientId: string;
  recipientNickname: string;
  content: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, COL), {
    ...params,
    senderGender: 'unknown',
    senderProfileImageUrl: null,
    isRead: false,
    sentAt: serverTimestamp(),
  });
  return ref.id;
}

/** Send DM to multiple recipients from a specified sender */
export async function sendBulkDM(params: {
  senderId: string;
  senderNickname: string;
  recipients: { uid: string; nickname: string }[];
  content: string;
}): Promise<number> {
  let count = 0;
  for (const r of params.recipients) {
    await sendDM({
      senderId: params.senderId,
      senderNickname: params.senderNickname,
      recipientId: r.uid,
      recipientNickname: r.nickname,
      content: params.content,
    });
    count++;
  }
  return count;
}

export async function markAsRead(dmId: string): Promise<void> {
  await updateDoc(doc(db, COL, dmId), { isRead: true, readAt: serverTimestamp() });
}

export async function markAsUnread(dmId: string): Promise<void> {
  await updateDoc(doc(db, COL, dmId), { isRead: false, readAt: null });
}

export async function deleteDM(dmId: string): Promise<void> {
  await deleteDoc(doc(db, COL, dmId));
}

export async function deleteMultipleDMs(ids: string[]): Promise<void> {
  const batch = writeBatch(db);
  ids.forEach(id => batch.delete(doc(db, COL, id)));
  await batch.commit();
}
